import { useEffect, useMemo, useState } from 'react'
import { DISTRETTI } from '../data/geodata'
import { CDC_WITH_AMBITO } from '../data/cdcAmbiti'
import type { AmbitoId } from '../data/ambitiStats'

const W = 700
const H = 500
const PADDING = 16
const FOCUS_BLEND_CENTROID = 0.72
const FOCUS_BLEND_BBOX = 0.28
const OPTICAL_CENTER_Y_OFFSET = -6

type LngLat = [number, number]

type DistrictGeometry = {
  type: 'Polygon' | 'MultiPolygon'
  coordinates: number[][][] | number[][][][]
}

interface DistrictFeature {
  type: 'Feature'
  properties: {
    id: string
    nome?: string
    color?: string
  }
  geometry: DistrictGeometry
}

interface DistrictCollection {
  type: 'FeatureCollection'
  features: DistrictFeature[]
}

interface DistrictData {
  id: string
  nome: string
  color: string
  rings: LngLat[][]
  labelPos: LngLat
  comuni: string[]
}

const DISTRICT_ORDER = ['monza', 'carate', 'desio', 'seregno', 'vimercate']
const LABEL_Y_OFFSETS: Record<string, number> = {
  monza: 50,
  carate: -80,
}

function getOuterRings(geometry: DistrictGeometry): LngLat[][] {
  if (geometry.type === 'Polygon') {
    const poly = geometry.coordinates as number[][][]
    if (!poly.length) return []
    return [poly[0] as LngLat[]]
  }

  const multi = geometry.coordinates as number[][][][]
  return multi
    .map(poly => poly[0] as LngLat[])
    .filter(ring => ring.length > 0)
}

function ringCentroid(ring: LngLat[]): LngLat {
  if (!ring.length) return [0, 0]
  const sum = ring.reduce(
    (acc, [lng, lat]) => ({ lng: acc.lng + lng, lat: acc.lat + lat }),
    { lng: 0, lat: 0 }
  )
  return [sum.lng / ring.length, sum.lat / ring.length]
}

function ringAreaCentroid(ring: LngLat[]): { area: number; centroid: LngLat } {
  if (ring.length < 3) return { area: 0, centroid: ringCentroid(ring) }

  let twiceArea = 0
  let centroidX = 0
  let centroidY = 0

  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i]
    const [x2, y2] = ring[(i + 1) % ring.length]
    const cross = x1 * y2 - x2 * y1
    twiceArea += cross
    centroidX += (x1 + x2) * cross
    centroidY += (y1 + y2) * cross
  }

  const area = twiceArea / 2
  if (Math.abs(area) < 1e-12) return { area: 0, centroid: ringCentroid(ring) }

  return {
    area,
    centroid: [centroidX / (3 * twiceArea), centroidY / (3 * twiceArea)],
  }
}

function districtFocusPoint(rings: LngLat[][], fallback: LngLat): LngLat {
  if (!rings.length) return fallback

  let totalWeight = 0
  let weightedLng = 0
  let weightedLat = 0

  for (const ring of rings) {
    const { area, centroid } = ringAreaCentroid(ring)
    const weight = Math.abs(area)
    if (weight <= 0) continue
    totalWeight += weight
    weightedLng += centroid[0] * weight
    weightedLat += centroid[1] * weight
  }

  if (totalWeight <= 0) return fallback
  return [weightedLng / totalWeight, weightedLat / totalWeight]
}

interface DistrictMapProps {
  onSelectDistrict: (id: string) => void
  onClearSelection?: () => void
  showCdcMarkers?: boolean
  sanitaryMarkers?: Array<{
    key: string
    nome: string
    ambitoId: AmbitoId
    lng: number
    lat: number
  }>
  showComuneCountInLegend?: boolean
  showLegend?: boolean
  selectedDistrictId?: string | null
  allowDistrictSelection?: boolean
  showInstructionText?: boolean
  onSelectCdc?: (nome: string) => void
  selectedCdcNome?: string | null
  onSelectSanitary?: (key: string) => void
  selectedSanitaryKey?: string | null
}

export default function DistrictMap({ onSelectDistrict, onClearSelection, showCdcMarkers = false, sanitaryMarkers = [], showComuneCountInLegend = true, showLegend = true, selectedDistrictId, allowDistrictSelection = true, showInstructionText = true, onSelectCdc, selectedCdcNome, onSelectSanitary, selectedSanitaryKey }: DistrictMapProps) {
  const [hovered, setHovered] = useState<string | null>(null)
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null)
  const [districts, setDistricts] = useState<DistrictData[]>([])
  const [hoveredMarkerKey, setHoveredMarkerKey] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768)

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (typeof selectedDistrictId === 'undefined') return
    setSelectedDistrict(selectedDistrictId)
    setHovered(selectedDistrictId)
  }, [selectedDistrictId])

  useEffect(() => {
    let mounted = true

    fetch('/data/distretti.geojson')
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((geo: DistrictCollection) => {
        const loaded = geo.features
          .map(feature => {
            const id = feature.properties?.id
            if (!id || !(id in DISTRETTI)) return null

            const rings = getOuterRings(feature.geometry)
            if (!rings.length) return null

            const meta = DISTRETTI[id]
            return {
              id,
              nome: feature.properties?.nome || meta.nome.replace('Distretto di ', ''),
              color: feature.properties?.color || meta.colorHex,
              rings,
              labelPos: ringCentroid(rings[0]),
              comuni: meta.comuni,
            } satisfies DistrictData
          })
          .filter((d): d is DistrictData => d !== null)
          .sort((a, b) => DISTRICT_ORDER.indexOf(a.id) - DISTRICT_ORDER.indexOf(b.id))

        if (mounted) setDistricts(loaded)
      })
      .catch(() => {
        if (mounted) setDistricts([])
      })

    return () => {
      mounted = false
    }
  }, [])

  const bounds = useMemo(() => {
    const points = districts.flatMap(d => d.rings.flat())
    if (!points.length) return null

    const lons = points.map(p => p[0])
    const lats = points.map(p => p[1])

    return {
      minLng: Math.min(...lons),
      maxLng: Math.max(...lons),
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats),
    }
  }, [districts])

  function toSVG(lng: number, lat: number): [number, number] {
    if (!bounds) return [0, 0]

    const spanLng = Math.max(bounds.maxLng - bounds.minLng, 0.000001)
    const spanLat = Math.max(bounds.maxLat - bounds.minLat, 0.000001)
    const x = ((lng - bounds.minLng) / spanLng) * (W - PADDING * 2) + PADDING
    const y = ((bounds.maxLat - lat) / spanLat) * (H - PADDING * 2) + PADDING
    return [x, y]
  }

  function poly(coords: LngLat[]): string {
    return coords.map(([lng, lat]) => toSVG(lng, lat).join(',')).join(' ')
  }

  const zoomDataByDistrict = useMemo(() => {
    const entries = districts.map(d => {
      const projectedRings = d.rings.map(r => r.map(([lng, lat]) => toSVG(lng, lat)))
      const points = projectedRings.flat()
      if (!points.length) {
        return [
          d.id,
          { centerX: W / 2, centerY: H / 2, scale: 1 }
        ] as const
      }

      const xs = points.map(p => p[0])
      const ys = points.map(p => p[1])
      const minX = Math.min(...xs)
      const maxX = Math.max(...xs)
      const minY = Math.min(...ys)
      const maxY = Math.max(...ys)
      const width = Math.max(1, maxX - minX)
      const height = Math.max(1, maxY - minY)
      const [focusLng, focusLat] = districtFocusPoint(d.rings, d.labelPos)
      const [centroidX, centroidY] = toSVG(focusLng, focusLat)
      const bboxCenterX = minX + width / 2
      const bboxCenterY = minY + height / 2
      const centerX = centroidX * FOCUS_BLEND_CENTROID + bboxCenterX * FOCUS_BLEND_BBOX
      const centerY = centroidY * FOCUS_BLEND_CENTROID + bboxCenterY * FOCUS_BLEND_BBOX + OPTICAL_CENTER_Y_OFFSET

      const targetWidth = W * 0.74
      const targetHeight = H * 0.74
      const scale = Math.max(1.35, Math.min(3.8, Math.min(targetWidth / width, targetHeight / height)))

      return [
        d.id,
        { centerX, centerY, scale }
      ] as const
    })

    return Object.fromEntries(entries)
  }, [districts, bounds])

  const viewportTransform = useMemo(() => {
    if (!selectedDistrict) return 'translate(0px, 0px) scale(1)'
    const zoom = zoomDataByDistrict[selectedDistrict]
    if (!zoom) return 'translate(0px, 0px) scale(1)'

    return `translate(${W / 2}px, ${H / 2}px) scale(${zoom.scale}) translate(${-zoom.centerX}px, ${-zoom.centerY}px)`
  }, [selectedDistrict, zoomDataByDistrict])

  const renderDistricts = useMemo(() => {
    let ordered = [...districts]

    if (hovered) {
      const hoveredDistrict = ordered.find(d => d.id === hovered)
      if (hoveredDistrict) {
        ordered = ordered.filter(d => d.id !== hovered)
        ordered.push(hoveredDistrict)
      }
    }

    if (selectedDistrict) {
      const selected = ordered.find(d => d.id === selectedDistrict)
      if (selected) {
        ordered = ordered.filter(d => d.id !== selectedDistrict)
        ordered.push(selected)
      }
    }

    return ordered
  }, [districts, hovered, selectedDistrict])

  const detailDistrictId = hovered || selectedDistrict

  const cdcMarkers = useMemo(() => {
    if (!showCdcMarkers) return [] as Array<{
      key: string
      x: number
      y: number
      active: boolean
      nome: string
      ambitoId: AmbitoId
    }>

    if (!districts.length) return [] as Array<{
      key: string
      x: number
      y: number
      active: boolean
      nome: string
      ambitoId: AmbitoId
    }>

    const grouped = new Map<AmbitoId, typeof CDC_WITH_AMBITO>()
    for (const item of CDC_WITH_AMBITO) {
      const list = grouped.get(item.ambitoId) ?? []
      list.push(item)
      grouped.set(item.ambitoId, list)
    }

    const markerList: Array<{
      key: string
      x: number
      y: number
      active: boolean
      nome: string
      ambitoId: AmbitoId
    }> = []

    for (const district of districts) {
      const cdcInDistrict = grouped.get(district.id as AmbitoId) ?? []
      if (!cdcInDistrict.length) continue

      cdcInDistrict.forEach((cdc, index) => {
        let x: number, y: number

        if (cdc.casa.posizione) {
          ;[x, y] = toSVG(cdc.casa.posizione.lng, cdc.casa.posizione.lat)
        } else {
          // fallback al centroide del distretto per case senza coordinate
          const [centerX, centerY] = toSVG(...district.labelPos)
          x = centerX
          y = centerY
        }

        markerList.push({
          key: `${district.id}-${cdc.casa.nome}-${index}`,
          x,
          y,
          active: cdc.casa.attiva === 1,
          nome: cdc.casa.nome,
          ambitoId: cdc.ambitoId,
        })
      })
    }

    return markerList
  }, [districts, bounds, showCdcMarkers])

  const renderedSanitaryMarkers = useMemo(() => {
    if (!sanitaryMarkers.length) return [] as Array<{
      key: string
      x: number
      y: number
      nome: string
      ambitoId: AmbitoId
    }>

    return sanitaryMarkers
      .filter(marker => !selectedDistrict || marker.ambitoId === selectedDistrict)
      .map(marker => {
        const [x, y] = toSVG(marker.lng, marker.lat)
        return {
          key: marker.key,
          x,
          y,
          nome: marker.nome,
          ambitoId: marker.ambitoId,
        }
      })
  }, [sanitaryMarkers, selectedDistrict, bounds])

  return (
    <div style={{ width: '100%', maxWidth: 750, margin: '0 auto' }}>
      {/* Header */}
      {showInstructionText && (
        <p
          style={{
            color: 'var(--text-1)',
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          Seleziona l'ambito
        </p>
      )}

      {/* SVG Map */}
      <div style={{ position: 'relative' }}>
        {selectedDistrict && allowDistrictSelection && (
          <button
            onClick={() => {
              setSelectedDistrict(null)
              setHovered(null)
              onClearSelection?.()
            }}
            style={{
              position: 'absolute',
              zIndex: 2,
              top: 10,
              left: 10,
              border: '1px solid var(--border-md)',
              background: 'var(--bg-1)',
              color: 'var(--text-0)',
              borderRadius: 8,
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,.08)',
            }}
          >
            ← Tutti gli ambiti
          </button>
        )}

        <svg
          viewBox={`0 0 ${W} ${H}`}
          style={{ width: '100%', height: 'auto', borderRadius: 8, border: '1px solid var(--border-md)', background: 'var(--bg-2)' }}
          onClick={() => {}}
        >
          <g
            style={{
              transform: viewportTransform,
              transformOrigin: '0 0',
              transition: 'transform .72s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            {renderDistricts.map(d => {
              const isHovered = hovered === d.id
              const isSelected = selectedDistrict === d.id
              const isDimmed = selectedDistrict !== null && !isSelected
              return (
                <g
                  key={d.id}
                  onClick={allowDistrictSelection
                    ? () => {
                        setSelectedDistrict(d.id)
                        setHovered(d.id)
                        onSelectDistrict(d.id)
                      }
                    : undefined}
                  onMouseEnter={allowDistrictSelection ? () => setHovered(d.id) : undefined}
                  onMouseLeave={allowDistrictSelection ? () => setHovered(null) : undefined}
                  style={{
                    cursor: allowDistrictSelection ? 'pointer' : 'default',
                    opacity: isDimmed ? 0.14 : 1,
                    transition: 'opacity .42s ease',
                  }}
                >
                  {d.rings.map((ring, idx) => (
                    <polygon
                      key={`${d.id}-${idx}`}
                      points={poly(ring)}
                      fill={d.color}
                      fillOpacity={isSelected || isHovered ? 0.9 : 0.62}
                      stroke={isSelected || isHovered ? '#1e293b' : '#fff'}
                      strokeWidth={isSelected || isHovered ? 3 : 2}
                      style={{
                        transition: 'fill-opacity .28s ease, stroke .28s ease, stroke-width .28s ease',
                        vectorEffect: 'non-scaling-stroke',
                        strokeLinejoin: 'round',
                        strokeLinecap: 'round',
                      }}
                    />
                  ))}
                </g>
              )
            })}

            <g style={{ pointerEvents: 'none' }}>
              {renderDistricts.map(d => {
                const isHovered = hovered === d.id
                const isSelected = selectedDistrict === d.id
                const isDimmed = selectedDistrict !== null && !isSelected
                const [lx, ly] = toSVG(...d.labelPos)
                const labelY = ly + (LABEL_Y_OFFSETS[d.id] ?? 0)

                return (
                  <text
                    key={`label-${d.id}`}
                    x={lx}
                    y={labelY}
                    textAnchor="middle"
                    fill="#fff"
                    fontWeight={800}
                    fontSize={isSelected ? 39 : isHovered ? 37 : 35}
                    stroke="#000"
                    strokeWidth={4}
                    paintOrder="stroke"
                    opacity={isDimmed ? 0.26 : 1}
                    style={{ transition: 'font-size .3s ease, opacity .3s ease' }}
                  >
                    {d.nome}
                  </text>
                )
              })}
            </g>

            {showCdcMarkers && (
                <g>
                  {cdcMarkers.map(marker => {
                    const isSelected = selectedCdcNome === marker.nome
                    const isHoveredMarker = marker.key === hoveredMarkerKey
                    return (
                      <g
                        key={marker.key}
                        transform={`translate(${marker.x} ${marker.y})`}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => setHoveredMarkerKey(marker.key)}
                        onMouseLeave={() => setHoveredMarkerKey(null)}
                        onClick={(e) => {
                          e.stopPropagation()
                          onSelectCdc?.(marker.nome)
                        }}
                      >
                        {!marker.active && !isSelected && (
                          <circle r={isMobile ? 18 : 13} fill="#dc2626" opacity={0.34}>
                            <animate attributeName="r" values={isMobile ? "18;28;18" : "13;22;13"} dur="1.8s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0.34;0;0.34" dur="1.8s" repeatCount="indefinite" />
                          </circle>
                        )}
                        <circle
                          r={isSelected ? (isMobile ? 24 : 18) : isHoveredMarker ? (isMobile ? 20 : 14) : (isMobile ? 18 : 12)}
                          fill={isSelected ? '#2563eb' : marker.active ? '#16a34a' : '#dc2626'}
                          stroke="#ffffff"
                          strokeWidth={isSelected ? 4 : 3}
                          style={{ transition: 'r 0.2s ease, fill 0.2s ease' }}
                        />
                        <path
                          d={isMobile ? "M -8 0 L -8 8 L 8 8 L 8 0 L 0 -7 Z" : "M -5.5 0 L -5.5 5.5 L 5.5 5.5 L 5.5 0 L 0 -5 Z"}
                          fill="#ffffff"
                          stroke="none"
                        />
                      </g>
                    )
                  })}
                </g>
            )}

            {renderedSanitaryMarkers.length > 0 && (
              <g>
                {renderedSanitaryMarkers.map(marker => {
                  const isSelectedMarker = selectedSanitaryKey === marker.key
                  const isHoveredMarker = hoveredMarkerKey === marker.key

                  return (
                    <g
                      key={marker.key}
                      transform={`translate(${marker.x} ${marker.y})`}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setHoveredMarkerKey(marker.key)}
                      onMouseLeave={() => setHoveredMarkerKey(null)}
                      onClick={(event) => {
                        event.stopPropagation()
                        onSelectSanitary?.(marker.key)
                      }}
                    >
                      <title>{marker.nome}</title>
                      <circle
                        r={isSelectedMarker ? (isMobile ? 22 : 16) : isHoveredMarker ? (isMobile ? 18 : 13) : (isMobile ? 15 : 10)}
                        fill={isSelectedMarker ? '#0891b2' : '#0ea5e9'}
                        stroke="#ffffff"
                        strokeWidth={isSelectedMarker ? 4 : 3}
                        style={{ transition: 'r 0.2s ease, fill 0.2s ease' }}
                      />
                      <path
                        d={isMobile ? 'M -2 -7 L 2 -7 L 2 -2 L 7 -2 L 7 2 L 2 2 L 2 7 L -2 7 L -2 2 L -7 2 L -7 -2 L -2 -2 Z' : 'M -1.5 -5 L 1.5 -5 L 1.5 -1.5 L 5 -1.5 L 5 1.5 L 1.5 1.5 L 1.5 5 L -1.5 5 L -1.5 1.5 L -5 1.5 L -5 -1.5 L -1.5 -1.5 Z'}
                        fill="#ffffff"
                      />
                    </g>
                  )
                })}
              </g>
            )}
          </g>
        </svg>
      </div>


    </div>
  )
}
